import {
  ArrowLeft,
  MapPin,
  Flag,
} from "lucide-react";

import {
  UserProfile_GetbymatchuserId,
  Admin_ITSpecialty_LIST,
  Admin_Skills_LIST,
} from "../../constants/adminData";

import {
  useEffect,
  useState,
  useCallback,
  useRef,
} from "react";

import {
  UserProfile,
  ITSpecialty,
  Skill,
} from "../../types/admin";

import { PhotoImage } from "../../components/common/PhotoImage";

import { calculateAge } from "../../utils/useUtil";

import { useNavigate } from "react-router-dom";

import ReportModal, {
  ReportType,
} from "../../components/common/ReportModal";

import CommentsModal from "../../components/common/CommentsModal";

import { apiVariables } from "../../services/api/apiVariables";

import { getToken } from "../../utils/tokenUtils";

import Toast from "../../components/Toast/Toast";

import { getSensitiveData } from "../../utils/secureStorage";

function getInitials(name: string): string {
  const parts = name
    .trim()
    .split(/\s+/)
    .filter(Boolean);

  if (parts.length >= 2) {
    return (
      parts[0][0] +
      parts[parts.length - 1][0]
    ).toUpperCase();
  }

  return name.substring(0, 2).toUpperCase();
}

function getProfileITSpecialtyId(profile: any): number {
  return Number(
    profile?.itSpecialtyId ??
      profile?.itspecialtyId ??
      profile?.specialtyId ??
      0
  );
}

function getProfileITSpecialtyName(profile: any): string {
  return (
    profile?.itSpecialtyName ||
    profile?.itspecialtyName ||
    ""
  );
}

function getSkillId(skill: any): number {
  return Number(
    skill?.skillId ??
      skill?.id ??
      skill?.userSkillId ??
      skill
  );
}

function getSkillName(skill: any): string {
  if (typeof skill === "string") {
    return skill;
  }

  return (
    skill?.skillName ||
    skill?.name ||
    skill?.title ||
    ""
  );
}

function getTechnologyName(interest: any): string {
  return (
    interest?.interestName ||
    interest?.name ||
    interest?.technologyName ||
    interest?.customExploreTechnologies ||
    "Technology"
  );
}

function getAvailabilityTypeName(item: any): string {
  return item?.availabilityTypeName || item?.name || item?.label || "";
}

function formatDate(value?: string | null): string {
  if (!value) return "";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return String(value).slice(0, 10);
  return date.toLocaleDateString("en-US", {
    day: "2-digit",
    month: "short",
    year: "numeric",
  });
}

function parseStoredProfile(value: string | null): any | null {
  if (!value) {
    return null;
  }

  try {
    return JSON.parse(value);
  } catch (error) {
    console.error("Invalid stored match profile:", error);
    return null;
  }
}

function mergeProfileData(
  fallbackProfile: any,
  apiProfile: any
) {
  const merged = { ...(fallbackProfile || {}) };

  Object.entries(apiProfile || {}).forEach(
    ([key, value]) => {
      if (value !== undefined && value !== null) {
        merged[key] = value;
      }
    }
  );

  return Object.keys(merged).length > 0
    ? merged
    : null;
}

interface MatchProfileProps {
  onOpenChat?: (receiverId: string, userName: string, photoId?: string) => void;
}

export default function MatchProfile({ onOpenChat }: MatchProfileProps) {
  const navigate = useNavigate();
  const hasLoadedProfileRef = useRef(false);
  const profileMountedRef = useRef(true);

  const [userData, setUserData] =
    useState<UserProfile | null>(null);

  const [selectedMatchData, setSelectedMatchData] =
    useState<any | null>(null);

  const [specialties, setSpecialties] =
    useState<ITSpecialty[]>([]);

  const [skills, setSkills] =
    useState<Skill[]>([]);

  const [isReportModalOpen, setIsReportModalOpen] =
    useState(false);

  const [reportType, setReportType] =
    useState<ReportType>("profile");

  const [toast, setToast] = useState<{
    message: string;
    type: "success" | "error";
  } | null>(null);

  const [isCommentsOpen, setIsCommentsOpen] =
    useState(false);

  const [photoId, setPhotoId] =
    useState<string>("");

  const [loading, setLoading] =
    useState<boolean>(true);

  const loadMyLikeStatus = useCallback(async (
    pid: string | number
  ) => {
    try {
      if (!pid) return;

      const url = `${apiVariables.CheckPhotoLike}/${pid}/likes/my`;

      const response = await fetch(url, {
        method: "GET",
        headers: {
          Authorization: `Bearer ${await getToken()}`,
        },
      });

      if (!response.ok) return;

      await response.json();
    } catch (error) {
      console.error(error);
    }
  }, []);

  const loadUserProfile = useCallback(async (
    selectedUserId: string,
    canUpdate: () => boolean = () => true
  ) => {
    if (!selectedUserId) return;

    try {
      if (canUpdate()) {
        setLoading(true);
      }

      const response =
        await UserProfile_GetbymatchuserId(
          selectedUserId
        );

      if (!canUpdate()) return;

      if (response?.success) {
        setUserData(response.data);

        const pid = String((response.data as any)?.photoId || "");

        if (pid) {
          setPhotoId(pid);
        }

        await loadMyLikeStatus(pid);
      }

      const [
        specialities,
        skillsResponse,
      ] = await Promise.all([
        Admin_ITSpecialty_LIST(),
        Admin_Skills_LIST(),
      ]);

      if (canUpdate()) {
        setSpecialties(
          Array.isArray(specialities?.data)
            ? specialities.data
            : []
        );
        setSkills(
          Array.isArray(skillsResponse?.data)
            ? skillsResponse.data
            : []
        );
      }
    } catch (error) {
      console.error(
        "Error loading profile:",
        error
      );
    } finally {
      if (canUpdate()) {
        setLoading(false);
      }
    }
  }, [loadMyLikeStatus]);

  useEffect(() => {
    profileMountedRef.current = true;

    const loadSelectedProfile = async () => {
      if (hasLoadedProfileRef.current) {
        return;
      }

      hasLoadedProfileRef.current = true;

      try {
        setLoading(true);

        const selectedUserId =
          (await getSensitiveData(
            "selectedMatchId"
          )) || "";
        const storedMatchProfile =
          parseStoredProfile(
            await getSensitiveData(
              "selectedMatchProfile"
            )
          );

        if (!profileMountedRef.current) return;

        if (storedMatchProfile) {
          setSelectedMatchData(storedMatchProfile);

          const storedPhotoId = String(
            storedMatchProfile?.photoId || ""
          );

          if (storedPhotoId) {
            setPhotoId(storedPhotoId);
          }
        }

        if (!selectedUserId) {
          setUserData(null);
          setPhotoId("");
          setLoading(false);
          return;
        }

        await loadUserProfile(
          selectedUserId,
          () => profileMountedRef.current
        );
      } catch (error) {
        console.error(
          "Error loading selected profile:",
          error
        );

        if (profileMountedRef.current) {
          setLoading(false);
        }
      }
    };

    loadSelectedProfile();

    return () => {
      profileMountedRef.current = false;
    };
  }, [loadUserProfile]);

  const getITSpecialtyName = (
    list: ITSpecialty[],
    id: number
  ) => {
    return (
      list.find(
        (item) =>
          item.itSpecialtyId === id
      )?.itSpecialtyName ?? ""
    );
  };

  const displayProfile = mergeProfileData(
    selectedMatchData,
    userData
  );

  const itSpecialtyName = displayProfile
    ? getProfileITSpecialtyName(displayProfile) ||
      getITSpecialtyName(
        specialties,
        getProfileITSpecialtyId(displayProfile)
      )
    : "";

  const profileSkills = Array.isArray(
    displayProfile?.skills
  )
    ? (displayProfile.skills as any[]).filter(
        (skill) => String(skill?.approvalStatus ?? "").toLowerCase() !== "rejected"
      )
    : [];

  const skillNames = profileSkills
    .map((skill) => {
      const directName = getSkillName(skill);

      if (directName) {
        return directName;
      }

      const skillId = getSkillId(skill);

      return (
        skills.find(
          (item) => item.skillId === skillId
        )?.skillName || ""
      );
    })
    .filter(Boolean);

  const profileInterests = Array.isArray(displayProfile?.interests)
    ? (displayProfile.interests as any[])
    : [];

  const technologyNames = profileInterests
    .map((interest) => getTechnologyName(interest))
    .filter(Boolean);

  const availabilityRows = Array.isArray(displayProfile?.availabilities)
    ? (displayProfile.availabilities as any[])
    : [];

  const primaryAvailability = availabilityRows[0] ?? null;

  const isAvailable = Boolean(
    primaryAvailability?.isAvailable ??
      primaryAvailability?.availabilityStatus === "Available"
  );

  const availabilityTypeNames = Array.from(
    new Set(
      (primaryAvailability?.availabilityTypeNames ??
        (primaryAvailability ? [getAvailabilityTypeName(primaryAvailability)] : []))
        .filter(Boolean)
    )
  ) as string[];

  // Hide exceptions that ended before the current week, matching CompleteProfileForm.
  const currentWeekStart = (() => {
    const monday = new Date();
    monday.setHours(0, 0, 0, 0);
    monday.setDate(monday.getDate() - ((monday.getDay() + 6) % 7));
    return monday.getTime();
  })();

  const availabilityExceptions = ((primaryAvailability?.exceptions ?? []) as any[]).filter((exception) => {
    if (!exception?.endDate) return true;
    const [year, month, day] = String(exception.endDate).slice(0, 10).split("-").map(Number);
    const endDate = new Date(year, month - 1, day).getTime();
    return Number.isNaN(endDate) || endDate >= currentWeekStart;
  });

  const calculatedAge = calculateAge(
    displayProfile?.dateOfBirth || ""
  );

  const age =
    displayProfile?.age ?? calculatedAge;

  const fullName = `${displayProfile?.firstName || ""} ${displayProfile?.lastName || ""}`.trim() || "Member";

  const location = displayProfile?.hometown || displayProfile?.currentLocation || "Location not added";

  const organization =
    displayProfile?.currentOranizationName ||
    displayProfile?.currentOrganizationName ||
    "";

  const experience = displayProfile?.totalExperienceInYears;

  const companyCount = displayProfile?.totalCompaniesHaveBeen;

  const handleStartConversation = () => {
    if (!displayProfile?.userId) return;

    const receiverId = displayProfile.userId;

    const userName = `${displayProfile.firstName} ${displayProfile.lastName}`;

    if (onOpenChat) {
      onOpenChat(receiverId, userName, photoId || undefined);
      return;
    }

    navigate(`/messageChat/${receiverId}/${encodeURIComponent(userName)}`);
  };

  const handleReportPhoto = () => {
    setReportType("photo");

    setIsReportModalOpen(true);
  };

  const handleSubmitReport = async (
    data: {
      reason: string;
      entityType: number;
      entityId: string;
    }
  ) => {
    if (!displayProfile?.userId) return;

    try {
      const reportingUserId =
        (await getSensitiveData(
          "userId"
        )) || "";

      const token = await getToken();

      const response = await fetch(
        apiVariables.REPORT_SUBMIT,
        {
          method: "POST",
          headers: {
            "Content-Type":
              "application/json",
            Authorization: `Bearer ${token}`,
          },
          body: JSON.stringify({
            reportingUserId,
            reportedUserId:
              displayProfile.userId,
            reportedEntityType:
              data.entityType,
            reportedEntityId: "",
            reportReason: data.reason,
          }),
        }
      );

      if (response.ok) {
        setToast({
          message:
            "Report submitted successfully.",
          type: "success",
        });

        setIsReportModalOpen(false);
      }
    } catch (error) {
      console.error(error);
    }
  };

  if (loading) {
    return (
      <div className="flex h-full min-h-[60vh] items-center justify-center text-sm text-gray-500">
        Loading profile...
      </div>
    );
  }

  return (
    <div className="space-y-5">
      {/* ── Page Header ── */}
      <div className="flex items-center gap-3">
        <button
          onClick={() => navigate(-1)}
          className="inline-flex items-center gap-1.5 rounded-lg px-2 py-1.5 text-sm font-semibold text-gray-600 transition hover:bg-gray-100 hover:text-gray-900"
        >
          <ArrowLeft className="h-4 w-4" />
          Back
        </button>
      </div>

      {/* ── Identity Hero ── */}
      <section className="rounded-lg border border-gray-200 bg-white p-5 shadow-sm">
        <div className="flex flex-col gap-4 sm:flex-row sm:items-center">
          <div className="relative h-24 w-24 flex-shrink-0 overflow-hidden rounded-full bg-gradient-to-b from-brand-primary to-brand-gray ring-4 ring-brand-primary/10">
            <PhotoImage
              photoId={photoId}
              alt={fullName}
              className="h-full w-full object-cover"
              fallback={
                <div className="flex h-full w-full items-center justify-center text-2xl font-semibold text-white">
                  {getInitials(fullName)}
                </div>
              }
            />

            {photoId && (
              <button
                onClick={handleReportPhoto}
                className="absolute bottom-0 right-0 rounded-full bg-white p-1.5 shadow-sm ring-1 ring-gray-200 transition hover:bg-gray-50"
                aria-label="Report photo"
              >
                <Flag className="h-3.5 w-3.5 text-brand-primary" />
              </button>
            )}
          </div>

          <div className="min-w-0 flex-1">
            <h1 className="truncate text-2xl font-bold text-gray-950">
              {fullName}
            </h1>
            <div className="mt-2 flex flex-wrap items-center gap-x-4 gap-y-1 text-sm text-gray-600">
              <span className="inline-flex items-center gap-1.5">
                <MapPin className="h-4 w-4 text-gray-400" />
                {location}
              </span>
              {itSpecialtyName && (
                <>
                  <span className="text-gray-300">•</span>
                  <span>{itSpecialtyName}</span>
                </>
              )}
              {organization && (
                <>
                  <span className="text-gray-300">•</span>
                  <span>{organization}</span>
                </>
              )}
            </div>

            <div className="mt-4">
              <button
                onClick={handleStartConversation}
                className="inline-flex h-11 items-center justify-center rounded-lg bg-brand-primary px-5 text-sm font-semibold text-white shadow-sm transition hover:bg-brand-hover"
              >
                Start a Private Conversation
              </button>
            </div>
          </div>
        </div>
      </section>

      {/* ── Professional Information ── */}
      <section className="rounded-lg border border-gray-200 bg-white p-5 shadow-sm">
        <h2 className="text-lg font-semibold text-gray-950">Professional Information</h2>
        <div className="mt-4 grid grid-cols-1 gap-x-8 gap-y-4 sm:grid-cols-2 lg:grid-cols-3">
          {[
            ['IT Specialty', itSpecialtyName || 'Not specified'],
            ['Total Experience', experience ? `${experience} years` : 'Not specified'],
            ['Number of Companies Worked At', companyCount ?? 'Not specified'],
            ['Current Organization', organization || 'Not specified'],
            ['Location', location],
          ].map(([label, value]) => (
            <div key={label}>
              <p className="text-xs font-semibold uppercase tracking-wide text-gray-500">{label}</p>
              <p className="mt-1 break-words text-sm font-medium text-gray-900">{value}</p>
            </div>
          ))}
        </div>
      </section>

      {/* ── Skills & Technologies ── */}
      <div className="grid grid-cols-1 gap-5 lg:grid-cols-2">
        <section className="rounded-lg border border-gray-200 bg-white p-5 shadow-sm">
          <h2 className="text-lg font-semibold text-gray-950">Skills</h2>
          <div className="mt-3 flex flex-wrap gap-2">
            {skillNames.length > 0 ? (
              skillNames.map((skillName, index) => (
                <span
                  key={`${skillName}-${index}`}
                  className="rounded-full border border-blue-100 bg-blue-50 px-3 py-1 text-sm font-medium text-blue-700"
                >
                  {skillName}
                </span>
              ))
            ) : (
              <p className="text-sm text-gray-500">No skills added.</p>
            )}
          </div>
        </section>

        <section className="rounded-lg border border-gray-200 bg-white p-5 shadow-sm">
          <h2 className="text-lg font-semibold text-gray-950">Technologies to Explore</h2>
          <div className="mt-3 flex flex-wrap gap-2">
            {technologyNames.length > 0 ? (
              technologyNames.map((name, index) => (
                <span
                  key={`${name}-${index}`}
                  className="rounded-full border border-emerald-100 bg-emerald-50 px-3 py-1 text-sm font-medium text-emerald-700"
                >
                  {name}
                </span>
              ))
            ) : (
              <p className="text-sm text-gray-500">No technologies added.</p>
            )}
          </div>
        </section>
      </div>

      {/* ── About Me ── */}
      {displayProfile?.shortBio && (
        <section className="rounded-lg border border-gray-200 bg-white p-5 shadow-sm">
          <h2 className="text-lg font-semibold text-gray-950">About Me</h2>
          <p className="mt-3 whitespace-pre-line break-words text-sm leading-6 text-gray-700">
            {displayProfile.shortBio}
          </p>
        </section>
      )}

      {/* ── Availability ── */}
      <section className="rounded-lg border border-gray-200 bg-white p-5 shadow-sm">
        <h2 className="text-lg font-semibold text-gray-950">Availability</h2>
        {primaryAvailability ? (
          <>
            <div className="mt-4 grid grid-cols-1 gap-4 sm:grid-cols-3">
              <div>
                <p className="text-xs font-semibold uppercase tracking-wide text-gray-500">Status</p>
                <span
                  className={`mt-2 inline-flex rounded-full px-3 py-1 text-sm font-semibold ${
                    isAvailable ? 'bg-green-50 text-green-700 ring-1 ring-green-100' : 'bg-orange-50 text-orange-700 ring-1 ring-orange-100'
                  }`}
                >
                  {isAvailable ? 'Available' : 'Not Available'}
                </span>
              </div>
              <div>
                <p className="text-xs font-semibold uppercase tracking-wide text-gray-500">Hours Per Week</p>
                <p className="mt-2 text-sm font-semibold text-gray-900">
                  {primaryAvailability?.defaultAvailableHoursPerWeek ?? primaryAvailability?.availableHoursPerWeek ?? 'Not specified'}
                </p>
              </div>
              <div>
                <p className="text-xs font-semibold uppercase tracking-wide text-gray-500">Available For</p>
                <div className="mt-2 flex flex-wrap gap-2">
                  {availabilityTypeNames.length > 0 ? (
                    availabilityTypeNames.map((typeName) => (
                      <span
                        key={typeName}
                        className="rounded-full bg-slate-100 px-2.5 py-1 text-xs font-semibold text-slate-700"
                      >
                        {typeName}
                      </span>
                    ))
                  ) : (
                    <span className="text-sm text-gray-500">Not specified</span>
                  )}
                </div>
              </div>
            </div>

            {availabilityExceptions.length > 0 && (
              <div className="mt-5">
                <p className="text-sm font-semibold uppercase tracking-wide text-gray-600">Exceptions</p>
                <div className="mt-2 space-y-2">
                  {availabilityExceptions.map((exception: any, index: number) => (
                    <div
                      key={exception.availabilityExceptionId ?? index}
                      className="rounded-lg border border-gray-200 bg-gray-50 px-3 py-2 text-sm text-gray-700"
                    >
                      {formatDate(exception.startDate)} – {formatDate(exception.endDate)} &nbsp;·&nbsp; {exception.availableHoursPerWeek} hrs/week
                    </div>
                  ))}
                </div>
              </div>
            )}
          </>
        ) : (
          <p className="mt-3 text-sm text-gray-500">Availability not specified.</p>
        )}
      </section>

      <ReportModal
        isOpen={isReportModalOpen}
        onClose={() =>
          setIsReportModalOpen(false)
        }
        onSubmit={handleSubmitReport}
        reportType={reportType}
        userName={fullName}
        profileImageId={
          displayProfile?.profileImageUrl || ""
        }
      />

      <CommentsModal
        isOpen={isCommentsOpen}
        onClose={() =>
          setIsCommentsOpen(false)
        }
        photoId={photoId}
      />

      {toast && (
        <Toast
          message={toast.message}
          type={toast.type}
          onClose={() =>
            setToast(null)
          }
        />
      )}
    </div>
  );
}
